import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Tag, FileText } from 'lucide-react';

interface BilingualContent {
  en: string;
  zh: string;
  zhTitle: string;
  zhExcerpt: string;
  zhTags: string[];
}

export const parseBilingualContent = (content: string): BilingualContent | null => {
  const enMatch = content.match(/<a id="en"><\/a>\s*([\s\S]*?)<a id="zh"><\/a>/);
  const zhSplit = content.split('<a id="zh"></a>');
  if (!enMatch || zhSplit.length < 2) return null;

  // 中文段开头可以带几行注释，给中文版单独的标题、摘要和标签：
  //   <!-- zh-title: … -->  <!-- zh-excerpt: … -->  <!-- zh-tags: 标签1 | 标签2 -->
  const meta: Record<string, string> = {};
  const en = enMatch[1].trim();
  const zh = zhSplit[1]
    .replace(/<!--\s*zh-(title|excerpt|tags):\s*([\s\S]*?)\s*-->\s*/g, (_, key, value) => {
      meta[key] = value.trim();
      return '';
    })
    .replace(/^\s*##\s*中文版\s*/, '')
    .replace(/^\s*\*\*Language\s*\/\s*语言:\*\*.*(\r?\n)+/, '')
    .trim();

  if (!en || !zh) return null;
  return {
    en,
    zh,
    zhTitle: meta.title || '',
    zhExcerpt: meta.excerpt || '',
    zhTags: meta.tags ? meta.tags.split('|').map(t => t.trim()).filter(Boolean) : []
  };
};

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  tags: string[];
  published_at: string;
  created_at: string;
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeLang, setActiveLang] = useState<'en' | 'zh'>(
    typeof window !== 'undefined' && window.location.hash === '#zh' ? 'zh' : 'en'
  );

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try API first
        let response;
        try {
          response = await fetch(`/api/blog/${slug}`);
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              const postData = {
                ...result.data,
                tags: typeof result.data.tags === 'string' 
                  ? JSON.parse(result.data.tags) 
                  : result.data.tags || []
              };
              setPost(postData);
              return;
            }
          }
        } catch (apiError) {
          console.log('API failed, trying localStorage:', apiError);
        }

        // Fallback to localStorage for local development
        const stored = localStorage.getItem('blog_posts');
        if (stored) {
          const allPosts = JSON.parse(stored);
          const foundPost = allPosts.find((p: any) => 
            p.slug === slug || p.id === slug
          );
          
          if (foundPost) {
            setPost({
              ...foundPost,
              tags: typeof foundPost.tags === 'string' 
                ? JSON.parse(foundPost.tags) 
                : foundPost.tags || []
            });
            return;
          }
        }

        throw new Error('Post not found');
      } catch (error) {
        console.error('Error fetching post:', error);
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const formatDate = (dateString: string, lang: 'en' | 'zh' = 'en') => {
    return new Date(dateString).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatContent = (content: string) => {
    // Simple markdown-like formatting
    return content
      .replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold text-gray-900 mb-6 mt-8">$1</h1>')
      .replace(/^## (.*$)/gm, '<h2 class="text-2xl font-bold text-gray-900 mb-4 mt-6">$1</h2>')
      .replace(/^### (.*$)/gm, '<h3 class="text-xl font-bold text-gray-900 mb-3 mt-4">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^\d+\. (.*$)/gm, '<li class="ml-4 list-decimal">$1</li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-purple-600 hover:text-purple-700 underline">$1</a>')
      .replace(/\n\n/g, '</p><p class="mb-4">')
      .replace(/^(?!<[h|u|o|a|s])/gm, '<p class="mb-4">')
      .replace(/<p class="mb-4"><\/p>/g, '');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Post Not Found
            </h1>
            <p className="text-gray-600">
              The blog post you're looking for doesn't exist or has been moved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const bilingual = parseBilingualContent(post.content);
  const lang: 'en' | 'zh' = bilingual ? activeLang : 'en';
  const englishTags: string[] = (typeof post.tags === 'string' ? JSON.parse(post.tags) : post.tags) || [];
  const shown = lang === 'zh' && bilingual
    ? {
        title: bilingual.zhTitle || post.title,
        excerpt: bilingual.zhExcerpt || post.excerpt,
        tags: bilingual.zhTags.length ? bilingual.zhTags : englishTags,
      }
    : { title: post.title, excerpt: post.excerpt, tags: englishTags };

  return (
    <div className="min-h-screen">
      <Helmet>
        <html lang={lang === 'zh' ? 'zh-CN' : 'en'} />
        <title>{shown.title} | Woodlogos Blog</title>
        <meta name="description" content={post.excerpt || post.title} />
        <link rel="canonical" href={`https://www.woodlogos.net/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.woodlogos.net/blog/${post.slug}`} />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 to-blue-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {shown.title}
            </h1>
            
            <div className="flex items-center justify-center space-x-6 text-gray-600 mb-8">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                <span>{formatDate(post.published_at || post.created_at, lang)}</span>
              </div>
            </div>

            {/* Tags */}
            {(() => {
              const tags = shown.tags;
              return tags && Array.isArray(tags) && tags.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mb-8">
                  {tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                    >
                      <Tag className="h-3 w-3 inline mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            {/* Excerpt */}
            {shown.excerpt && (
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <p className="text-lg text-gray-700 italic">
                  {shown.excerpt}
                </p>
              </div>
            )}

            {/* Main Content */}
            {(() => {
              if (!bilingual) {
                return (
                  <div
                    className="text-gray-800 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: formatContent(post.content)
                    }}
                  />
                );
              }

              const switchLang = (lang: 'en' | 'zh') => {
                setActiveLang(lang);
                if (typeof window !== 'undefined') {
                  window.history.replaceState(null, '', `#${lang}`);
                }
              };

              return (
                <>
                  <div className="flex items-center gap-2 mb-8 not-prose">
                    <button
                      type="button"
                      onClick={() => switchLang('en')}
                      aria-pressed={activeLang === 'en'}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        activeLang === 'en'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      English
                    </button>
                    <button
                      type="button"
                      onClick={() => switchLang('zh')}
                      aria-pressed={activeLang === 'zh'}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        activeLang === 'zh'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      中文
                    </button>
                  </div>
                  <div
                    id="en"
                    lang="en"
                    className="text-gray-800 leading-relaxed"
                    style={{ display: activeLang === 'en' ? 'block' : 'none' }}
                    dangerouslySetInnerHTML={{
                      __html: formatContent(bilingual.en)
                    }}
                  />
                  <div
                    id="zh"
                    lang="zh"
                    className="text-gray-800 leading-relaxed"
                    style={{ display: activeLang === 'zh' ? 'block' : 'none' }}
                    dangerouslySetInnerHTML={{
                      __html: formatContent(bilingual.zh)
                    }}
                  />
                </>
              );
            })()}

            {/* Footer */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="text-sm text-gray-500 text-center">
                {lang === 'zh'
                  ? `发布于 ${formatDate(post.published_at || post.created_at, 'zh')}`
                  : `Published on ${formatDate(post.published_at || post.created_at)}`}
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
